import { microApi } from 'app/services/api';
import { ListResponse } from 'app/types';
import { IInspectionJobEvent } from 'app/types/interfaces/InspectionJobEvent';
import { omit } from 'lodash';
import qs from 'qs';
import { Report } from './types/Report';

const END_POINTS = 'admin/inspections';
export const inspectionApi = microApi.injectEndpoints({
  endpoints: (build) => ({
    getInspections: build.query<
      {
        data: Array<any>;
        pagination: {
          page: number;
          pageSize: number;
          pages: number;
          total: number;
        };
      },
      {
        current: number;
        pageSize: number;
        searchBy?: string;
        keywords?: string;
        sortBy?: string;
        status?: string[];
        result?: string[];
      }
    >({
      query: (params) => {
        const { current, pageSize, searchBy, keywords, ...rest } = params;
        const payload = {
          pageNo: current,
          pageSize: pageSize,
          ...rest
        };
        if (searchBy && keywords) {
          payload['searchBy'] = searchBy;
          payload['keywords'] = keywords;
        }
        const queryString = qs.stringify(payload, {
          encodeValuesOnly: false,
          indices: false
        });
        return {
          url: `/${END_POINTS}${queryString ? '?' + queryString : ''}`,
          method: 'GET'
        };
      },
      transformResponse: (baseQueryReturnValue: ListResponse<any>) => {
        return {
          pagination: baseQueryReturnValue.data.pagination,
          data: baseQueryReturnValue.data.list
        };
      }
    }),
    getInspection: build.query({
      query: (id) => ({
        url: `/${END_POINTS}/${id}`,
        method: 'GET'
      }),
      transformResponse: (baseQueryReturnValue) => {
        return (baseQueryReturnValue as any).data;
      }
    }),
    getInspectorAvailable: build.query({
      query: (params) => {
        const queryString = qs.stringify(
          {
            pageNo: params.current,
            pageSize: params.pageSize,
            startDate: params.startDate,
            endDate: params.endDate
          },
          {
            encodeValuesOnly: false,
            indices: false
          }
        );
        return {
          url: `/${END_POINTS}/inspectors/available${
            queryString ? '?' + queryString : ''
          }`,
          method: 'GET'
        };
      },
      transformResponse: (baseQueryReturnValue) => {
        return (baseQueryReturnValue as any).data;
      }
    }),
    getInspectorWithJob: build.query({
      query: (params) => {
        const queryString = qs.stringify(
          {
            pageNo: params.current,
            pageSize: params.pageSize,
            startDate: params.startDate,
            endDate: params.endDate,
            inspectorIds: params.inspectorIds,
            ...omit(params, [
              'current',
              'pageSize',
              'startDate',
              'endDate',
              'inspectorIds'
            ])
          },
          {
            encodeValuesOnly: false,
            indices: false
          }
        );
        return {
          url: `/${END_POINTS}/inspectors/with-job${
            queryString ? '?' + queryString : ''
          }`,
          method: 'GET'
        };
      },
      transformResponse: (
        baseQueryReturnValue: ListResponse<IInspectionJobEvent>
      ) => {
        return {
          pagination: baseQueryReturnValue.data.pagination,
          data: baseQueryReturnValue.data.list
        };
      }
    }),
    getInspectionSampleSize: build.query<
      {
        aql: {
          critical: number;
          major: number;
          minor: number;
        };
        sampleSize: string;
      },
      {
        qty: number; //quantity
        inspectionLevel: string;
        inspectionAqlCritical?: string;
        inspectionAqlMajor?: string;
        inspectionAqlMinor?: string;
      }
    >({
      query: (params) => {
        const queryString = qs.stringify(params, {
          encodeValuesOnly: false,
          indices: false
        });
        return {
          url: `/${END_POINTS}/sample-size${
            queryString ? '?' + queryString : ''
          }`
        };
      },
      transformResponse: (baseQueryReturnValue) => {
        return (baseQueryReturnValue as any).data;
      }
    }),
    bookInspection: build.mutation({
      query: ({ inspectionId, data }) => {
        return {
          url: `/${END_POINTS}/${inspectionId}`,
          method: 'POST',
          body: data
        };
      }
    }),
    getInspectionReportDetail: build.query<Report, string | number>({
      query: (inspectionId: number | string) => {
        return {
          url: `/${END_POINTS}/${inspectionId}/report`,
          method: 'GET'
        };
      },
      transformResponse: (baseQueryReturnValue) => {
        return (baseQueryReturnValue as any).data;
      }
    }),
    getInspectionDocument: build.query<
      any,
      {
        inspectionId: number | string;
        params?: {
          pagination: {
            pageNo: number;
            pageSize: number;
          };
        };
      }
    >({
      query: ({ inspectionId, params }) => {
        const queryString = qs.stringify(params?.pagination, {
          encodeValuesOnly: false,
          indices: false
        });
        return {
          url: `/inspections/${inspectionId}/documents${
            queryString ? '?' + queryString : ''
          }`,
          method: 'GET'
        };
      },
      transformResponse: (baseQueryReturnValue) => {
        return (baseQueryReturnValue as any).data;
      }
    }),
    deleteInspectionDocument: build.mutation({
      query: ({ inspectionId, documentId }) => {
        return {
          url: `/inspections/${inspectionId}/documents/${documentId}`,
          method: 'DELETE'
        };
      }
    }),
    createInspectionDocument: build.mutation<
      any,
      {
        inspectionId: number;
        documentsIds: number[];
      }
    >({
      query: ({ inspectionId, documentsIds }) => {
        return {
          url: `/inspections/${inspectionId}/documents`,
          method: 'POST',
          body: {
            documentListIds: documentsIds
          }
        };
      }
    }),
    cancelInspection: build.mutation<any, string>({
      query: (inspectionId) => {
        return {
          url: `${END_POINTS}/${inspectionId}/cancel`,
          method: 'POST',
          body: {}
        };
      }
    }),
    fetchInspectionProgress: build.query({
      query: (inspectionId: string) => {
        return {
          url: `inspections/${inspectionId}/progress`,
          method: 'GET'
        };
      }
    }),
    exportInspectionData: build.query({
      query(params: {
        content: 'jobList' | 'reportDetails';
        startDate: Date;
        endDate: Date;
      }) {
        const { content, ...range } = params;
        const exports = {
          jobList: 'iqc/exports',
          reportDetails: 'job-report-details/exports'
        };
        return {
          url: `/inspections/${exports[content]}`,
          method: 'GET',
          params: {
            ...range
          }
        };
      },
      transformResponse(baseQueryReturnValue: any) {
        return baseQueryReturnValue.data;
      }
    })
  })
});

export const {
  useLazyGetInspectionsQuery,
  useLazyGetInspectionQuery,
  useLazyGetInspectorAvailableQuery,
  useLazyGetInspectionSampleSizeQuery,
  useLazyGetInspectorWithJobQuery,
  useBookInspectionMutation,
  useGetInspectorWithJobQuery,
  useGetInspectionReportDetailQuery,
  useGetInspectionDocumentQuery,
  useLazyGetInspectionDocumentQuery,
  useDeleteInspectionDocumentMutation,
  useCreateInspectionDocumentMutation,
  useCancelInspectionMutation,
  useFetchInspectionProgressQuery,
  useLazyExportInspectionDataQuery
}: typeof inspectionApi = inspectionApi;
